import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiClient, RECAPTCHA_TOKEN_HEADER } from '@/lib/api-client';
import { useActiveSearch, type PolicySearchCriteria } from '@/lib/active-search';
import { QUERY_KEYS } from '@/lib/query-keys';
import { executeRecaptcha } from '@/lib/recaptcha';
import type { ApiError } from '@/types/api.types';
import type { PolicyDetails } from '../types/policy.types';

/**
 * Policyholder Information for the active search (US-006).
 *
 * A query rather than a mutation so the result survives navigating away and back within the session
 * (AC-003). It is keyed on the criteria, so a new search replaces the previous policy rather than
 * merging with it (US-013 AC-002).
 */
export function usePolicySearch(): UseQueryResult<PolicyDetails, ApiError> {
  const { data } = useActiveSearch();
  const criteria = data ?? null;

  return useQuery<PolicyDetails, ApiError>({
    queryKey: [...QUERY_KEYS.policyDetails, criteria],
    queryFn: () => {
      if (criteria === null) {
        // Unreachable while `enabled` is false; the guard is what keeps the criteria type honest.
        throw new Error('No active policy search.');
      }

      return fetchPolicy(criteria);
    },
    enabled: criteria !== null,
    staleTime: 5 * 60_000,
  });
}

async function fetchPolicy(criteria: PolicySearchCriteria): Promise<PolicyDetails> {
  const recaptchaToken = await executeRecaptcha('policy_search');
  const response = await apiClient.post<PolicyDetails>('/api/policies/search', criteria, {
    headers: { [RECAPTCHA_TOKEN_HEADER]: recaptchaToken },
  });

  return response.data;
}
